<template>
  <div v-if="loading" class="text-center mt-5 font-2xl text-muted">
    <i class="fal fa-spinner fa-spin" /> Project Form Data Loading...
  </div>
  <div v-else class="animated fadeIn">
    <b-form @submit.prevent="submitForm">
      <b-card no-body>
        <b-card-header>
          <span class="text-muted text-uppercase">Project</span>
        </b-card-header>

        <div class="py-3 px-4">
          <b-form-group label="Customer" label-cols="2" invalid-feedback="Required" :state="!$v.form_data.customer_id.$invalid">
            <b-form-input v-model.lazy="filter" placeholder="Search for a customer..." debounce="300" />
            <b-alert v-if="!filter" show class="py-1 m-0 mt-3">
              {{ customers.length ? "Choose" : "Search" }} a customer.
            </b-alert>
            <b-alert v-if="filter && !customers.length" show class="py-1 m-0 mt-3">
              No matches found.
            </b-alert>

            <custom-form-radio-group
              v-if="customers.length"
              v-model="form_data.customer_id"
              :options="[
                ...customers.map(i => ({
                  text: i.short_name,
                  value: i.id,
                  icon: 'user'
                })),
              ]"
              :state="!$v.form_data.customer_id.$invalid"
              class="mt-3"
              @change="value => loadContactsAndLocations(value)"
            />
          </b-form-group>

          <b-form-group label="Contact" label-cols="2" invalid-feedback="Required" :state="!$v.form_data.customer_id.$invalid">
            <b-overlay v-if="loadingContactsAndLocations" show spinner-small class="py-3" />
            <custom-form-radio-group
              v-else-if="contacts.length"
              v-model="form_data.customer_contact_id"
              :options="
                contacts.map(i => ({
                  text: formatContactName(i),
                  value: i.id,
                  icon: 'id-card',
                }))
              "
              buttons
            />
            <b-alert v-else-if="form_data.customer_id" show class="py-1 m-0">
              No customer contacts available.
            </b-alert>
            <b-alert v-else show class="py-1 m-0">
              Select a customer to see available contacts.
            </b-alert>
          </b-form-group>

          <b-form-group label="Location" label-cols="2">
            <b-overlay v-if="loadingContactsAndLocations" show spinner-small class="py-3" />
            <custom-form-radio-group
              v-else-if="locations.length"
              v-model="form_data.customer_location_id"
              :options="
                locations.map(i => ({
                  text: i.fullAddress(),
                  value: i.id,
                  icon: 'home'
                }))
              "
              buttons
            />
            <b-alert v-else-if="form_data.customer_id" show class="py-1 m-0">
              No customer locations available.
            </b-alert>
            <b-alert v-else show class="py-1 m-0">
              Select a customer to see available locations.
            </b-alert>
          </b-form-group>
        </div>
      </b-card>

      <!-- validation error -->
      <b-row v-if="invalid" class="mt-4">
        <b-col>
          <b-alert variant="danger" show>
            <i class="fa fa-exclamation-triangle" /> Please review the form and complete all required fields.
          </b-alert>
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <b-button
            type="submit"
            class="my-4 float-right py-3 px-5"
            variant="success"
          >
            <i class="fal fa-save" /> Save
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </div>
</template>

<script>
import { getCollection } from '@/shared/fetch-utils'
import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'
import CustomFormRadioGroup from '@/components/shared/form/CustomFormRadioGroup'
import CustomFormCheckboxGroup from '@/components/shared/form/CustomFormCheckboxGroup'
import CustomerModel from '@/models/crm/CustomerModel'

export default {
  name: 'ProjectForm',
  components: {
    CustomFormRadioGroup,
    CustomFormCheckboxGroup
  },
  mixins: [validationMixin],
  props: {
    project: {
      type: Object,
      default: () => null
    }
  },
  data () {
    return {
      loading: false,
      filter: null,
      loadingContactsAndLocations: false,
      customers: [],
      contacts: [],
      locations: [],
      form_data: {
        customer_id: null,
        customer_contact_id: null,
        customer_location_id: null
      }
    }
  },
  validations: {
    form_data: {
      customer_id: { required },
      customer_contact_id: { required },
      customer_location_id: { required }
    }
  },
  computed: {
    invalid () {
      return this.$v && this.$v.$dirty && this.$v.$invalid
    }
  },
  watch: {
    filter () {
      this.loadCustomers()
    }
  },
  created () {
    this.loading = true

    this.init()

    this.loading = false
  },
  methods: {
    displayError ({ response }) {
      const { status, data } = response

      this.$bvToast.toast(`Code: ${status}. ${data.error}`, {
        title: 'Server Error.',
        variant: 'danger'
      })
    },
    async loadCustomers () {
      try {
        const collection = await getCollection({
          collection_type: 'customer',
          filters: [
            {
              logic: 'filterContains',
              field: 'short_name',
              value: this.filter
            },
            {
              logic: 'setLimit',
              value: 10
            }
          ]
        })

        this.customers = collection.models
      } catch ({ response }) {
        this.displayError(response)
      }
    },
    async loadContactsAndLocations (customer_id) {
      if (!this.customers.find(({ id }) => id === customer_id)) {
        const customer = new CustomerModel({ id: customer_id })
        customer.fetch().then(() => {
          this.customers.push(customer)
        })
      }

      this.loadingContactsAndLocations = true

      const filters = [
        {
          logic: 'filterEquals',
          field: 'customer_id',
          value: customer_id

        }
      ]

      try {
        const contacts = await getCollection({
          collection_type: 'customer_contact',
          filters,
          message: this.$bvToast
        })
        const locations = await getCollection({
          collection_type: 'customer_location',
          filters,
          message: this.$bvToast
        })

        this.contacts = contacts.models
        this.locations = locations.models
        this.loadingContactsAndLocations = false
      } catch ({ response }) {
        this.displayError(response)
      }
    },
    /**
     * format contact name
     * @param {*} contact_data
     */
    formatContactName (contact_data) {
      const parts = []

      if (contact_data.first_name) {
        parts.push(contact_data.first_name)
      }
      if (contact_data.last_name) {
        parts.push(contact_data.last_name)
      }
      if (contact_data.email) {
        if (parts.length) {
          parts.push('' + contact_data.email + '')
        } else {
          parts.push(contact_data.email)
        }
      }

      return parts.join(' ')
    },
    submitForm () {
      this.$v.$touch()
      if (this.invalid) return

      this.$emit('submit', this.form_data)
    },
    async init () {
      if (this.project && this.project.id) {
        _.keys(this.form_data).forEach((key) => {
          if (this.project[key] !== undefined) {
            this.form_data[key] = this.project[key]
          }
        })

        const collection = await getCollection({
          collection_type: 'customer',
          filters: [
            {
              logic: 'filterEquals',
              field: 'id',
              value: this.project.customer_id
            },
            {
              logic: 'setLimit',
              value: 10
            }
          ]
        })

        this.customers = collection.models

        this.loadContactsAndLocations(this.project.customer_id)
      }
    }
  }
}
</script>
